import { ClockCircleOutlined, LeftOutlined, RightOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Calendar, Card, Col, Empty, Row, Space, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";
import Api from "../../../services/api";

const { Title, Text } = Typography;

const navigationMap = {
    "829855ba-6a28-4013-8440-20ff97078389": (documentId) => `/documentcontrol/details/${documentId}`,
    "b2b427ee-5199-4459-83fd-d28b3d2c18d2": (documentId) => `/documentcontrol/details/${documentId}`,
    "df3cb0ad-9e0a-44e1-898c-3e3a5df1483e": (id) => `/formbuilder/view-answer/${id}`,
    "ad89b18a-802e-4a51-94ae-bf73e7329a68": (formId, id) => `/formbuilder/apply/${formId}/${id}`,
};

const taskTypeConfig = {
    "829855ba-6a28-4013-8440-20ff97078389": { label: "Treinamento de documento", color: "#ffc800" },
    "df3cb0ad-9e0a-44e1-898c-3e3a5df1483e": { label: "Aprovação de Tarefa", color: "#60a5fa" },
    "ad89b18a-802e-4a51-94ae-bf73e7329a68": { label: "Preenchimento de formulário", color: "#34d399" },
    "b2b427ee-5199-4459-83fd-d28b3d2c18d2": { label: "Revisão de documento", color: "#ffc800" },
};

const statusMap = {
    Atrasadas: "late",
    Pendentes: "open",
    "Home.WaitingApproval": "approval",
};

const TaskCard = ({ task, t }) => {
    const cfg = taskTypeConfig[task.taskTypeId.toLowerCase()] || { label: "Outro", color: "#999999" };
    const navigate = useNavigate();

    const handleClick = () => {
        if (task.status === "Recurrency") {
            navigate(`/formbuilder/recurrency/${task.formScheduleRecurrencyId}/${dayjs(task.scheduleDate).format("YYYY-MM-DD")}/${task.scheduleSequence}`);
            return;
        }

        const key = task.taskTypeId.toLowerCase();
        const navigateFn = navigationMap[key];
        if (navigateFn) {
            if (key === "ad89b18a-802e-4a51-94ae-bf73e7329a68") {
                navigate(navigateFn(task.formId, task.id));
            } else if (key === "829855ba-6a28-4013-8440-20ff97078389" || key === "b2b427ee-5199-4459-83fd-d28b3d2c18d2") {
                navigate(navigateFn(task.documentId)); // <-- agora usa documentId
            } else {
                navigate(navigateFn(task.id));
            }
        }
    };

    return (
        <Card
            style={{ height: "100%", cursor: "pointer" }}
            bodyStyle={{ padding: 16 }}
            onClick={handleClick}
        >
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Space direction="horizontal" size={4} style={{ width: "100%" }}>
                    {task.status === "Recurrency" && (
                        <Tooltip title={t('Recurrency')}>
                            <div
                                style={{
                                    backgroundColor: "#4085c6",
                                    color: "#fff",
                                    fontWeight: 500,
                                    borderRadius: 8,
                                    padding: "2px 8px",
                                    fontSize: 12,
                                    display: "inline-block",
                                }}
                            >
                                <ClockCircleOutlined />
                            </div>
                        </Tooltip>
                    )}
                    <div
                        style={{
                            backgroundColor: cfg.color,
                            color: "#fff",
                            fontWeight: 500,
                            borderRadius: 8,
                            padding: "2px 8px",
                            fontSize: 12,
                            display: "inline-block",
                        }}
                    >
                        {cfg.label}
                    </div>
                </Space>
                <Text strong>
                    {task.status !== "Recurrency" && <>#{task.friendlyId} – </>}
                    {task.name}
                </Text>
                {task.description && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {task.description}
                    </Text>
                )}
                <Space size={4}>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <div style={{ marginLeft: 4 }}>
                        <Text style={{ fontSize: 12, color: "#888" }}>
                            {t('CreatedBy')}: <strong>{task.insertUserName}</strong>
                        </Text>
                        <br />
                        <Text style={{ fontSize: 12, color: "#888" }}>
                            {t('Task.DueDate2')} <strong>{dayjs(task.scheduleDate).format("YYYY-MMM-DD")}</strong>
                        </Text>
                    </div>
                </Space>
            </Space>
        </Card>
    );
};

const TaskBoardView = () => {
    const { t } = useTranslation();

    const [view, setView] = useState("lista");
    const [tasksByStatus, setTasksByStatus] = useState({ open: [], late: [], approval: [] });
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [calendarViewDate, setCalendarViewDate] = useState(dayjs());

    useEffect(() => {
        const fetchTasks = async (date) => {
            setLoading(true);
            try {
                const res = await Api.get("DashboardCustomFlowy/getTasksGroupedByStatus", {
                    params: { year: date.year(), month: date.month() + 1 },
                });
                const data = res.data?.response || {};
                setTasksByStatus({ open: data.open || [], late: data.late || [], approval: data.approval || [] });
            } catch (err) {
                console.error("Erro ao buscar tarefas:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchTasks(calendarViewDate);
    }, [calendarViewDate]);

    const allTasks = Object.values(tasksByStatus).flat();
    const tasksForDate = (date) => allTasks.filter((t) => dayjs(t.scheduleDate).isSame(date, "day"));
    const tasksForSelectedDate = tasksForDate(selectedDate);

    const renderDateCell = (date) => {
        const tasks = tasksForDate(date);
        return (
            <div style={{ padding: 4, textAlign: "center", fontSize: 11 }}>
                <div
                    style={{
                        backgroundColor: date.isSame(selectedDate, "day") ? "#1677ff" : "transparent",
                        color: date.isSame(selectedDate, "day") ? "#fff" : undefined,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        margin: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 500,
                    }}
                >
                    {date.date()}
                </div>
                {tasks.length > 0 && (
                    <div style={{ marginTop: 4, display: "flex", justifyContent: "center", gap: 2 }}>
                        {tasks.map((task) => {
                            const cfg = taskTypeConfig[task.taskTypeId.toLowerCase()] || { color: "#999" };
                            return (
                                <span
                                    key={task.friendlyId}
                                    style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        backgroundColor: cfg.color,
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
                <Col>
                    <Space direction="horizontal" align="center" size={8} style={{ width: "100%" }}>
                        <Title level={3} style={{ marginBottom: 0 }}>
                            {t('History.MyTasks')}
                        </Title>
                        <Button.Group size="small" style={{ marginTop: 8 }}>
                            <Button type={view === "lista" ? "primary" : "default"} onClick={() => setView("lista")}>
                                {t('List.Title')}
                            </Button>
                            <Button
                                type={view === "calendario" ? "primary" : "default"}
                                onClick={() => setView("calendario")}
                            >
                                {t('Home.TabTitle.Schedule')}
                            </Button>
                        </Button.Group>
                    </Space>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Text type="secondary" style={{ marginBottom: 12, display: "block" }}>
                        Acompanhe suas tarefas por status ou por data.
                    </Text>
                </Col>
            </Row>

            {view === "lista" && (
                <Row gutter={12}>
                    {Object.entries(statusMap).map(([label, key]) => (
                        <Col key={key} xs={24} md={8}>
                            <Title level={4}>{t(label)}</Title>
                            <div style={{ maxHeight: 400, overflowY: "auto", paddingRight: 4 }}>
                                {loading ? (
                                    <Loading top={0} />
                                ) : (
                                    <Space direction="vertical" style={{ width: "100%" }} size={16}>
                                        {tasksByStatus[key].length > 0
                                            ? tasksByStatus[key].map((task) => <TaskCard key={task.friendlyId} task={task} t={t} />)
                                            : <Empty description="Sem tarefas" />}
                                    </Space>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>
            )}

            {view === "calendario" && (
                <Row gutter={12}>
                    <Col xs={24} md={10}>
                        <Calendar
                            fullscreen={false}
                            value={calendarViewDate}
                            onPanelChange={(date) => setCalendarViewDate(date)}
                            onSelect={(date) => {
                                setSelectedDate(date);
                                setCalendarViewDate(date);
                            }}
                            dateFullCellRender={renderDateCell}
                            headerRender={({ value, onChange }) => (
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "6px 12px",
                                    }}
                                >
                                    <Button
                                        icon={<LeftOutlined />}
                                        type="text"
                                        size="small"
                                        onClick={() => onChange(value.subtract(1, "month"), "month")}
                                    />
                                    <Title level={5} style={{ margin: 0 }}>
                                        {value.format("MMMM YYYY")}
                                    </Title>
                                    <Button
                                        icon={<RightOutlined />}
                                        type="text"
                                        size="small"
                                        onClick={() => onChange(value.add(1, "month"), "month")}
                                    />
                                </div>
                            )}
                        />
                    </Col>

                    <Col xs={24} md={14}>
                        <Title level={4} style={{ marginBottom: 8 }}>
                            {t('Home.History')} {selectedDate.format("YYYY-MMM-DD")}
                        </Title>
                        <Space direction="vertical" style={{ width: "100%" }} size={16}>
                            {loading ? (
                                <Loading top={0} />
                            ) : tasksForSelectedDate.length > 0 ? (
                                tasksForSelectedDate.map((task) => (
                                    <TaskCard key={task.friendlyId} task={task} t={t} />
                                ))
                            ) : (
                                <Empty description="Nenhuma tarefa para este dia" />
                            )}
                        </Space>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default TaskBoardView;
